






















































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate-3';
import isEqual from 'lodash/isEqual';

import { setFocusOnFirstInput } from '@/utils/setFocusOnFirstInput';
import segmentIo from '@/utils/segment.io';

import { BusinessOnboardingYourDigitalCoinsForm } from '@/models/business-onboarding';
import { BUSINESS_ONBOARDING_UUID_CREATION } from '@/store/modules/businessOnboarding';

import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import ColorPicker from '@/componentsV2/molecules/ColorPicker/ColorPicker.vue';
import Input from '@/componentsV2/molecules/Inputs/TextField.vue';
import ImageUploadBlock from '@/componentsV2/molecules/Inputs/ImageUploadBlock.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';
import { colorDefaultData } from '@/componentsV2/molecules/ColorPicker/colorsDictionary';

import SignUpService from '@/api/SingupService';

import flagKeys from '@/constants/flagKeys';
import { isExternalOauthSessionOnline } from '@/router/router-utils';
import eventBus from '@/utils/eventBus';

const initialFormState = () => ({
  color: colorDefaultData.colorHex,
  colorKey: colorDefaultData.colorKey,
  coinShowFront: true,
  isLoadingImg: false,
  coinName: 'Enter the name'
});

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    FilledButton,
    ColorPicker,
    Input,
    ImageUploadBlock,
    Typography
  }
})
export default class CoinCustomizationForm extends Vue {
  @Prop({ default: () => ({}) })
  defaultFormData?: BusinessOnboardingYourDigitalCoinsForm;
  @Prop({ default: false }) isLoading?: boolean;
  @Prop({ default: null }) existingBusinessId?: string;
  @Prop({ default: '' }) uidCoin?: string;
  @Prop() labelActionBtn?: string;
  @Prop({ default: false }) uploadFailed?: boolean;
  @Prop({ default: false }) submitDisabled?: boolean;
  @Prop({ default: true }) showSubmitBtn?: boolean;
  @Prop({ default: true }) showTitle?: boolean;

  @Watch('formIsEqual', { immediate: true })
  onFormIsEqualChange(newState: boolean) {
    this.$emit('is-equal', newState);
  }

  signUpService = new SignUpService();

  formData: BusinessOnboardingYourDigitalCoinsForm =
    this.defaultFormData || initialFormState();
  formDataToCompare = {
    ...initialFormState(),
    ...this.defaultFormData
  };

  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  get labelActionBtnText() {
    return this.labelActionBtn
      ? this.$t(`${this.labelActionBtn}`)
      : this.$t(`${this.tPrefix}.btnNextLabel`);
  }

  get business() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  get isLogged() {
    const token = this.signUpService.getCredentials();

    return !!token || this.$store.getters['manager/isImpersonated'];
  }

  get tPrefix() {
    return 'common.components.interactiveCoin';
  }

  get uuid() {
    return (
      localStorage.getItem(BUSINESS_ONBOARDING_UUID_CREATION) ||
      (this.uidCoin ? this.uidCoin : '')
    );
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get coinImageIsOptional() {
    return this.flags.includes(flagKeys.npoCoinImageOptional);
  }

  get formIsEqual() {
    return isEqual(
      this.getDataToCompare(this.formData),
      this.getDataToCompare(this.formDataToCompare)
    );
  }

  handleColorPickerInput(colorhex: string, colorKey: string) {
    this.formData.colorKey = colorKey;
  }

  getDataToCompare(data: Record<string, any>) {
    const filteredData = Object.entries(data).filter(([key]) =>
      ['color', 'image'].includes(key)
    );

    return Object.fromEntries(filteredData);
  }

  handleRetry() {
    this.onSubmit();
    this.$emit('retry-clicked');
  }

  async getRecaptcha(route: string, featureFlag: string) {
    let recaptcha: string | undefined;
    if (this.flags.includes(featureFlag)) {
      recaptcha = await this.$recaptcha(route);
    }
    return recaptcha;
  }

  onSelectedImage() {
    segmentIo.track('Coin Image Selected', {
      'Signup Online': !!isExternalOauthSessionOnline()
    });
  }

  onUpdateLogo() {
    if (this.formData.image && !this.formData.image.imageUrl) {
      segmentIo.track('Coin Image Restored', {
        'is Oauth Image': !!this.formData.isOauthImage,
        'Signup Online': !!isExternalOauthSessionOnline()
      });
    }

    if (this.formData.coinShowFront) {
      return;
    }

    if (this.existingBusinessId) {
      this.formData.coinShowFront = true;
      return;
    }
    this.$store.dispatch('businessOnboarding/TOOGLE_COIN');
  }

  async onSubmit() {
    const formRef = this.$refs.observer;
    const formIsValid = await formRef.validate();

    const { image } = formRef.errors;

    if (image.length) {
      const uploadImageContainer = document.getElementById(
        'npo-image-upload-row'
      )!;

      uploadImageContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });

      return;
    }

    if (!formIsValid) {
      setFocusOnFirstInput('input[aria-invalid="true"]');

      return;
    }

    if (this.formIsEqual && !this.formData.isOauthImage) {
      this.$emit('next-step', this.formData);

      return;
    }

    this.$emit('submit', this.formData);
  }

  mounted() {
    this.formData.coinShowFront = true;
    eventBus.$on('submitCoinUpdates', this.onSubmit);
  }

  beforeDestroy() {
    eventBus.$off('submitCoinUpdates', this.onSubmit);
  }
}
